import {
  queryOptions,
  useQueries,
  useQuery,
  useSuspenseQueries,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { create, keyResolver } from "@yornaath/batshit";
import type { Borrower } from "shared";
import { Api } from "@/api.config";
import { QueryKeys } from "@/LinkGen";

const batcher = create({
  fetcher: async (IDs: number[]) => {
    return Api.Borrower(IDs).then((res) => res.data);
  },
  resolver: keyResolver("ID"),
});

export function useBorrowerList() {
  const { data } = useSuspenseQuery({
    queryKey: QueryKeys.Borrower.List(),
    queryFn: () => Api.BorrowerList().then((res) => res.data),
  });

  return data;
}

function borrowerQueryOptions(ID: number | null | undefined) {
  return queryOptions({
    queryKey: QueryKeys.Borrower(ID),
    queryFn: () => {
      if (!ID) {
        return null;
      }

      return batcher.fetch(ID);
    },
  });
}

export function useSuspenseBorrower(ID: number): Borrower;
export function useSuspenseBorrower(ID: null): null;
export function useSuspenseBorrower(ID: undefined): null;
export function useSuspenseBorrower(
  ID: number | null | undefined,
): Borrower | null;
export function useSuspenseBorrower(ID: number | null | undefined) {
  const { data } = useSuspenseQuery(borrowerQueryOptions(ID));

  return data;
}

export function useBorrowersSuspense(IDs: number[]) {
  return useSuspenseQueries({
    queries: IDs.map(borrowerQueryOptions),
    combine: (results) => results.map((x) => x.data as Borrower),
  });
}

export function useBorrowers(IDs: number[]): {
  data: Borrower[];
  isLoading: boolean;
};
export function useBorrowers(IDs: number[]) {
  const queries = useQueries({
    queries: IDs.map((ID) => borrowerQueryOptions(ID)),
  });

  const data = queries.map((x) => x.data);
  const isLoading = queries.some((x) => x.isLoading);

  return { data, isLoading };
}

export function useBorrowerSearch(query: string) {
  return useQuery({
    queryKey: QueryKeys.Borrower.Search(query),
    queryFn: () => Api.BorrowerSearch(query).then((res) => res.data),
    enabled: query.length >= 1,
    staleTime: 0, // enforce data is always fresh :)
  });
}
